import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Document, Page, pdfjs } from 'react-pdf';
import { GlobalWorkerOptions } from 'pdfjs-dist';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdfjs/pdf.worker.min.js`;


const MagazineReaderPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const location = useLocation();
  const pdfFile = location.state?.pdfUrl;
  
  // Number of free pages to preview before subscription prompt
  const FREE_PREVIEW_PAGES = location.state?.displayPages;;

  // currentPage is zero-indexed internally but displayed as pageNumber + 1 in react-pdf
  const [currentPage, setCurrentPage] = useState(0);

  // Number of pages in the PDF document
  const [numPages, setNumPages] = useState(null);

  // State to prevent rapid flipping of pages
  const [isFlipping, setIsFlipping] = useState(false);

  // Control subscription modal visibility
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Responsive page width state (max 900px or 95% of viewport width)
  const [pageWidth, setPageWidth] = useState(Math.min(window.innerWidth * 0.95, 900));

  // Permission check for premium content access
  const canReadPremium = isAuthenticated && user?.is_premium && user?.subscription_status === 'active';

  

  // The PDF file URL to load
  // const pdfFile = 'https://justurbane.in/uploads/articles/just-urbane-august-2025-e-magazine.pdf';
   
  console.log('Loaded PDF URL:', pdfFile);
  console.log('FREE PREVIEW PAGES:', FREE_PREVIEW_PAGES);

  
  // Handle successful document load and set number of pages
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Navigate to next page with subscription check and page bounds
  const nextPage = () => {
    if (isFlipping) return;

    if (!canReadPremium && currentPage >= FREE_PREVIEW_PAGES - 1) {
      setShowSubscriptionModal(true);
      return;
    }

    if (currentPage < numPages - 1) {
      setIsFlipping(true);
      setCurrentPage(currentPage + 1);
      setTimeout(() => setIsFlipping(false), 250);
    }
  };

  // Navigate to previous page with page bounds check
  const prevPage = () => {
    if (isFlipping) return;

    if (currentPage > 0) {
      setIsFlipping(true);
      setCurrentPage(currentPage - 1);
      setTimeout(() => setIsFlipping(false), 250);
    }
  };

  // Close PDF reader and navigate to home
  const closeReader = () => {
    navigate('/');
  };

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') nextPage();
      if (e.key === 'ArrowLeft') prevPage();
      if (e.key === 'Escape') closeReader();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, numPages, canReadPremium]);

  // Lock scroll when PDF reader is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Responsive update of pageWidth on window resize
  useEffect(() => {
    const handleResize = () => {
      setPageWidth(Math.min(window.innerWidth * 0.95, 900));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!pdfFile) {
    return <p className="p-4 text-white">No magazine selected</p>;
  }


  return (
    <div className="fixed top-0 left-0 w-screen h-screen bg-black z-50 overflow-hidden">
      {/* Close Button */}
      <button
        onClick={closeReader}
        className="absolute top-5 right-5 z-50 bg-black/70 p-3 rounded-full hover:bg-black/90"
        aria-label="Close Reader"
      >
        <X size={24} color="white" />
      </button>

      {/* PDF Document Container with flex centering */}
      <div className="w-full h-full flex items-center justify-center relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.25 }}
            className="w-full h-full flex items-center justify-center overflow-auto"
          >
            <Document
              file={pdfFile}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div className="text-white">Loading PDF...</div>}
            >
              {/* Page component with controlled width for responsive sizing */}
              <Page
                pageNumber={currentPage + 1} // react-pdf pageNumber is 1-based
                width={pageWidth} // responsive width with max size limit
                renderAnnotationLayer={false}
                renderTextLayer={false}
                className={!canReadPremium && currentPage >= FREE_PREVIEW_PAGES ? 'blur-xl' : ''}
              />
            </Document>

            {/* Premium content overlay blur if restricted */}
            {!canReadPremium && currentPage >= FREE_PREVIEW_PAGES && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-md z-20"
              >
                <Crown size={80} color="#b8860b" />
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={prevPage}
        className="absolute left-5 top-1/2 transform -translate-y-1/2 z-40 bg-black/70 hover:bg-black/90 p-3 rounded-full cursor-pointer"
        aria-label="Previous Page"
      >
        <ChevronLeft size={28} color="white" />
      </button>

      <button
        onClick={nextPage}
        className="absolute right-5 top-1/2 transform -translate-y-1/2 z-40 bg-black/70 hover:bg-black/90 p-3 rounded-full cursor-pointer"
        aria-label="Next Page"
      >
        <ChevronRight size={28} color="white" />
      </button>

      {/* Page Indicator */}
      <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-5 py-2 rounded-xl z-30">
        Page {currentPage + 1} of {numPages}
      </div>

      {/* Subscription Modal */}
      <AnimatePresence>
        {showSubscriptionModal && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={() => setShowSubscriptionModal(false)}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white p-10 rounded-2xl text-center max-w-md"
            >
              <div className="text-6xl mb-4">👑</div>
              <h3 className="text-2xl font-bold mb-2">Premium Content</h3>
              <p className="mb-6">Subscribe now to continue reading all pages.</p>
              <div className="flex justify-center gap-4">
                <Link to="/pricing" className="bg-amber-500 px-6 py-3 rounded font-bold">
                  Subscribe
                </Link>
                <button
                  onClick={() => setShowSubscriptionModal(false)}
                  className="bg-gray-200 px-6 py-3 rounded font-bold"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MagazineReaderPage;
